"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.updateReportRequestSchema = exports.createReportRequestSchema = exports.reportTimelineRequestSchema = exports.reportRowsRequestSchema = exports.runReportRequestSchema = exports.reportSchema = exports.reportSnapshotSchema = exports.reportRowSchema = exports.reportDefinitionSchema = exports.REPORT_MAX_GROUPS = exports.REPORT_CHARTS = exports.MONEY_METRICS = exports.ALLOCATION_METRICS = exports.WORKDAY_METRICS = exports.REPORT_METRICS = exports.DIMENSIONS_FOR = exports.REPORT_DIMENSIONS = exports.MONEY_SOURCES = exports.REPORT_SOURCES = void 0;
exports.isMoneyMetric = isMoneyMetric;
exports.listSpecOf = listSpecOf;
const zod_1 = require("zod");
const identifier_1 = require("../identifier");
/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ Reports — C15's wire shapes.                                              │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * `main.jsx` 2465–2508 (the arithmetic) and 3447–3655 (the builder and the saved list). The mockup
 * keeps its definition in one `S.rep` object and recomputes the whole report on every keystroke;
 * `0021_reports.sql` puts the definition in typed columns and says why.
 *
 * ⚠️ **Decision P-3 governs this file.** A saved report stores its RESULTS as well as its
 * definition, and is **private to its author** — the two halves are load-bearing together. Read the
 * header of `0021_reports.sql` before changing anything here.
 */
const charts_1 = require("./charts");
const periods_1 = require("./periods");
const timeline_1 = require("./timeline");
const columns_1 = require("./columns");
const wireDateSchema = zod_1.z.string().regex(/^\d{4}-\d{2}-\d{2}$/);
/**
 * The mockup's `REP_SRC` — five, and the last three are C60's.
 *
 * ⚠️⚠️ **THE THREE MONEY SOURCES ARE NOT "MORE TABLES TO GROUP BY" — THEY CHANGE WHAT A ROW IS.**
 * A worklog report counts hours; a `lineitem` report counts DELIVERABLES, and its hours are the
 * hours booked against them. The count measure says so: *Bejegyzésszám* and *Sortételszám* are
 * different questions, and a reader who thinks a line-item report is a worklog report with extra
 * columns will read every figure on it wrong.
 */
exports.REPORT_SOURCES = [
    'worklog',
    'task',
    'contract',
    'lineitem',
    'occurrence',
    /**
     * ⚠️⚠️ 17 Oct 2026 · **MUNKAIDŐ — A PERSON'S WORKING DAYS, NOT A TABLE OF RECORDS.** Each row is a
     * person-day from the working-day kernel (the one the Logolás grid reads): what the schedule
     * expected, what was logged, what was leave. Its measure that matters is *missing hours* —
     * Kockázatkezelés's *Logolási hiányok*, which this source retired. It has no list layout; its
     * figures mean something only added up over a period.
     */
    'workday',
    /**
     * 26 Oct 2026 · ⚠️ ADDED under rule 8 — *Erőforrások*: the allocations, what is PLANNED. Its rows
     * are allocation days, its measures planned hours and allocations, and soft bookings are in or
     * out by its own filter (`ReportFilters.softBookings`). Behind `project.view`, as allocations are.
     */
    'allocation',
];
/** ⚠️ The three that carry money, named once so no branch has to list them again. */
exports.MONEY_SOURCES = ['contract', 'lineitem', 'occurrence'];
/**
 * The mockup's `DIMS`, with its `risk` generalised to `metafield`.
 *
 * ⚠️ The mockup labels that entry *"Kockázat (metamező)"* — a metafield, named as one. Grouping by
 * a metafield's value is what the label already says; a dimension literally called `risk` over three
 * Hungarian string constants would have put demo data in the schema. See `0021_reports.sql`.
 */
/**
 * ⚠️ **`role` is C31's** — the mockup's *Projekt szerep*. The four the mockup also lists
 * (`contract`, `lineitem`, `occurrence`, `risk`) are C38's, and adding one here without its
 * `dimensionOf` branch is a report that groups everything into `none`.
 */
exports.REPORT_DIMENSIONS = [
    'project',
    'client',
    'assignee',
    'status',
    'metafield',
    'role',
    /* C60 · the four the mockup's `DIMS` also lists. */
    'contract',
    'lineitem',
    'scope',
    'occurrence',
    /**
     * C60.1 · ⚠⚠ **THE MOCKUP'S `risk`, RESTORED — AND THE NOTE ABOVE IS NOW HALF WRONG.** It
     * argued that a dimension called `risk` would be *"three Hungarian string constants"* in the
     * schema, which was true when the only risk in the product was a metafield somebody typed. C60
     * made it a computed integer with boundaries in `RISK_BANDS`, so grouping by it groups by
     * arithmetic rather than by demo data. The metafield dimension stays: they answer different
     * questions and always did.
     */
    'risk',
    /**
     * 17 Oct 2026 · a worklog's TASK — Kockázatkezelés's *Ismeretlen jegyek* is the unknown hours
     * grouped by task. Worklogs also reach `lineitem` and `contract` now, through the task's line item.
     */
    'task',
    /* 26 Oct 2026 · an allocation's hard or soft booking — the `allocation` source only. */
    'booking',
];
/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ ⚠️⚠️ WHICH DIMENSIONS A SOURCE CAN ACTUALLY GROUP BY.                      │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * ⚠️⚠️ **A DIMENSION THE SOURCE CANNOT REACH GROUPS EVERYTHING INTO `'none'`** — one bar labelled
 * *"no value"*, which reads as a broken report rather than as an impossible question. The mockup
 * filters its own `repDimMenu` the same way, and this is that filter as data so the picker and the
 * server cannot disagree about it.
 *
 * ⚠️ **`occurrence` belongs to the occurrence source alone**, and `lineitem`/`scope` to the line
 * item: a contract has many of each, so grouping a contract report by line item would mean one
 * contract appearing in several rows with its revenue counted once in each.
 */
exports.DIMENSIONS_FOR = {
    /* C60.1 · ⚠⚠ **`risk` IS ON EVERY SOURCE, BECAUSE EVERY SOURCE HANGS OFF A PROJECT.** A
       worklog, a task, a contract and a line item all belong to one, and the project carries the
       band — so *"hours by RISK"* and *"revenue by RISK"* are the same question asked of two
       measures: how much of this rides on projects whose numbers we cannot trust. */
    worklog: [
        'project',
        'client',
        'assignee',
        'status',
        'metafield',
        'role',
        'risk',
        'task',
        'lineitem',
        'contract',
    ],
    task: ['project', 'client', 'assignee', 'status', 'metafield', 'role', 'risk'],
    contract: ['project', 'client', 'assignee', 'status', 'contract', 'risk'],
    lineitem: ['project', 'client', 'assignee', 'status', 'contract', 'lineitem', 'scope', 'risk'],
    occurrence: ['project', 'client', 'assignee', 'status', 'contract', 'occurrence', 'risk'],
    /* 17 Oct 2026 · a working day belongs to a person and to nothing else. */
    workday: ['assignee'],
    /* 26 Oct 2026 · an allocation is a person on a line item, so on its contract and project too. */
    allocation: ['project', 'client', 'assignee', 'role', 'contract', 'lineitem', 'risk', 'booking'],
};
/**
 * The mockup's `repAggOptions`. `estimate` is its `est`.
 *
 * ⚠⚠ **THE TWO OVERTIME MEASURES ARE WORKLOG-ONLY AND THEY ARE NOT THE SAME NUMBER.**
 * `overtime` is the approved overtime HOURS; `overtimeCost` is those hours × the multiplier that
 * was frozen at approval (`0111` · P3-5). Three hours at 1.5× is 3 and 4.5 — the first answers
 * *"how much did people work beyond their schedule"* and the second *"what do we owe for it"*, and
 * a report that showed one under the other's label would be wrong by exactly the multiplier.
 *
 * ⚠️ **A PENDING claim counts for NOTHING in both.** `approved_multiplier` is null until somebody
 * decides, because nothing is owed yet — counting a request as an hour worked would let anybody
 * inflate a report by filing one.
 */
exports.REPORT_METRICS = [
    'hours',
    'count',
    'estimate',
    'overtime',
    'overtimeCost',
    /* C60 · the money measures, behind `report.money`. */
    'revenue',
    'budget',
    /**
     * 17 Oct 2026 · Munkaidő's own. `expected` is what the schedule asked of the days that were not
     * leave; `leave` the schedule's hours on the days that were; `missing` expected minus logged,
     * floored at zero PER PERIOD — ten hours on Monday pay off Tuesday, a good month does not pay off
     * a bad one; `missingCost` those hours at the person's cost rate, behind `report.money`. On
     * Munkaidő `hours` is what was logged.
     */
    'expected',
    'missing',
    'leave',
    'missingCost',
];
/** The measures Munkaidő answers, and the only source that answers the last four. */
exports.WORKDAY_METRICS = [
    'hours',
    'expected',
    'missing',
    'leave',
    'missingCost',
];
/**
 * ⚠️⚠️ **THE TWO THAT NEED `report.money`, AND THE PERMISSION GATES THE MEASURE, NOT THE SOURCE.**
 * Somebody without it can still report on contracts and line items — counting them, summing their
 * hours — and simply cannot pick revenue or budget. Gating the source would hide the structure
 * along with the money, which is not what *"they should not see the figures"* means.
 */
/** 26 Oct 2026 · what an allocation report measures: planned hours, and how many allocations. */
exports.ALLOCATION_METRICS = ['hours', 'count'];
exports.MONEY_METRICS = ['revenue', 'budget', 'missingCost'];
function isMoneyMetric(metric) {
    return exports.MONEY_METRICS.includes(metric);
}
/**
 * ⚠️ **C59 moved the catalogue to `charts.ts`**, where it grew from two to twenty-five and gained
 * the rules about which of them a given report can actually draw. Re-exported here because every
 * existing importer reaches for it through this module.
 */
var charts_2 = require("./charts");
Object.defineProperty(exports, "REPORT_CHARTS", { enumerable: true, get: function () { return charts_2.REPORT_CHARTS; } });
/**
 * How many groups a report returns.
 *
 * ⚠️ **The mockup's own `.slice(0, 8)`** — eight bars is what its chart is drawn for. Ours reports
 * `groupCount` alongside, so a truncated report can say *"8 of 23"* instead of quietly looking
 * complete. Silent truncation reads as "we covered everything" when it did not.
 */
exports.REPORT_MAX_GROUPS = 8;
/**
 * The definition — everything the mockup keeps in `S.rep`.
 *
 * ⚠️ **`chart` IS part of the definition and the table toggle is NOT.** The mockup's `cfg` carries
 * `chart` and its `repTable` / `repControls` live in component state; a saved report should reopen
 * as the shape its author chose, and should not reopen with somebody's rail collapsed.
 */
exports.reportDefinitionSchema = zod_1.z
    .object({
    source: zod_1.z.enum(exports.REPORT_SOURCES),
    dimension: zod_1.z.enum(exports.REPORT_DIMENSIONS),
    /** Required exactly when `dimension === 'metafield'`; the refinement below enforces it. */
    dimensionMetafieldId: zod_1.z.uuid().nullable(),
    metric: zod_1.z.enum(exports.REPORT_METRICS),
    chart: zod_1.z.enum(charts_1.REPORT_CHARTS),
    /**
     * ⚠️ **Both ends independently nullable, because an open interval is one of the mockup's three
     * range states** — its `repRangeNote` renders *"nyitott intervallum"* for exactly that.
     *
     * ⚠️⚠️ **WHICH DATE COLUMN THE PERIOD FILTERS DEPENDS ON THE SOURCE, AND THE MOCKUP DOES NOT
     * DECIDE IT.** A worklog has one obvious date (`worked_on`). A task has three candidates, and
     * the mockup's task branch simply **ignores the period** — its date pickers stay on screen doing
     * nothing. We filter a task report on **`due_on`**, and the screen labels the control with the
     * column it uses, because a visible control that does nothing is the specific failure this
     * project has shipped before. Flagged as an addition: `created_at` was the other candidate, and
     * "work due in this window" is the reading a forecasting product wants.
     *
     * ⚠️ Consequence, stated because it surprises: on a task report with `metric: 'hours'` the
     * period selects the TASKS and the hours are then all-time for those tasks. "Tasks due in Q3,
     * and the hours booked against them" is coherent; "hours logged in Q3" is a worklog report.
     */
    periodFrom: wireDateSchema.nullable(),
    periodTo: wireDateSchema.nullable(),
    /** The three filters the mockup's `repFilterAddMenu` offers, and no more. */
    filterProjectId: zod_1.z.uuid().nullable(),
    /**
     * ⚠️ **The mockup labels this *"Felelős / rögzítő"*, and the duality is real rather than sloppy
     * wording**: on a worklog report it is who logged the time, on a task report it is the assignee.
     * The `assignee` dimension splits the same way, following the mockup's `w ? w.u : t.as`.
     */
    filterMemberId: zod_1.z.uuid().nullable(),
    filterMetafieldId: zod_1.z.uuid().nullable(),
    /**
     * ⚠️ A string, compared as a string. That is what a `select` metafield holds and what the
     * mockup's risk filter does; a number or date metafield is not offerable as a filter for the
     * same reason, and the service refuses one rather than comparing `'8'` to `8`.
     */
    filterMetafieldValue: zod_1.z.string().max(200).nullable(),
    /**
     * ┌──────────────────────────────────────────────────────────────────┐
     * │ 16 Oct 2026 · THE LIST LAYOUT — `columns.ts` carries the argument.  │
     * └──────────────────────────────────────────────────────────────────┘
     *
     * ⚠️ **All four OPTIONAL, and absent means what every report saved before them meant**: the
     * summary layout, no list columns, no sort, no list filters. A definition written by an older
     * client, the demo seed or the agent's tool stays valid without knowing they exist.
     *
     * ⚠️ `metric` and `dimension` stay required on a list report too: they are what the builder
     * shows on switching back to the summary, and the columns are not a replacement for them.
     */
    layout: zod_1.z.enum(columns_1.REPORT_LAYOUTS).optional(),
    /** The list's columns, in order — catalogue keys and `mf:<id>`. */
    columns: zod_1.z.array(zod_1.z.string().min(1).max(64)).max(40).optional(),
    sort: columns_1.reportSortSchema.nullable().optional(),
    /** The list's filters. ⚠️ Honoured by BOTH layouts — a summary narrowed here is narrowed the same. */
    filters: columns_1.reportFiltersSchema.optional(),
    /**
     * 17 Oct 2026 · the preset the period was chosen with. A rolling one (`month`, `last3Months`,
     * …) is resolved against today on every run (`resolvedPeriod`); absent or `custom` means the
     * two dates are the period, as every report saved before it.
     */
    period: zod_1.z.enum(periods_1.REPORT_PERIODS).optional(),
    /** 17 Oct 2026 · the timeline layout's rows, periods and extras — `timeline.ts`. */
    timeline: timeline_1.timelineSpecSchema.optional(),
})
    /**
     * ⚠️ **A list needs a column, and each one has to be one its source can show.** A key the
     * catalogue does not know would be an empty column on every row; refusing it at the boundary
     * names the column instead.
     */
    .refine((value) => value.layout !== 'list' ||
    ((value.columns ?? []).length > 0 &&
        new Set(value.columns).size === (value.columns ?? []).length &&
        (value.columns ?? []).every((key) => (0, columns_1.reportColumn)(value.source, key) !== null || (0, columns_1.metafieldIdOfColumn)(key) !== null)), { error: 'a list needs distinct columns its source can show', path: ['columns'] })
    /** ⚠️ Sorting by a column that is not on screen would order rows by something invisible. */
    .refine((value) => value.sort === undefined ||
    value.sort === null ||
    ((value.columns ?? []).includes(value.sort.column) &&
        ((0, columns_1.metafieldIdOfColumn)(value.sort.column) !== null ||
            (0, columns_1.reportColumn)(value.source, value.sort.column)?.sortable === true)), { error: 'the sort column must be a sortable column of the list', path: ['sort'] })
    .refine((value) => value.filters === undefined ||
    columns_1.REPORT_FILTER_KEYS.every((key) => (value.filters?.[key] ?? []).length === 0 ||
        columns_1.REPORT_FILTERS_FOR[value.source].includes(key)), { error: 'this source cannot be filtered that way', path: ['filters'] })
    /**
     * 17 Oct 2026 · ⚠️ ***None* is a worklog or task filter value.** The money sources filter their
     * own rows — a contract always has itself — and a `'none'` there would be a non-id reaching a
     * uuid cast.
     */
    .refine((value) => value.source === 'worklog' ||
    value.source === 'task' ||
    columns_1.REPORT_FILTER_KEYS.every((key) => !(value.filters?.[key] ?? []).includes(columns_1.REPORT_FILTER_NONE)), { error: 'only worklog and task reports can filter for none', path: ['filters'] })
    .refine((value) => (value.dimension === 'metafield') === (value.dimensionMetafieldId !== null), {
    error: 'a metafield dimension needs a metafield, and only a metafield dimension may carry one',
    path: ['dimensionMetafieldId'],
})
    .refine((value) => (value.filterMetafieldId === null) === (value.filterMetafieldValue === null), {
    error: 'a metafield filter needs both a field and a value',
    path: ['filterMetafieldValue'],
})
    /**
     * ⚠️ **`estimate` is task-only, and the mockup agrees** — `repAggOptions` appends it only for
     * tasks, and `setRepSrc` demotes it to `hours` when the source switches. A worklog has no estimate.
     */
    .refine((value) => value.metric !== 'estimate' || value.source === 'task', {
    error: 'the estimate metric needs the task source',
    path: ['metric'],
})
    /**
     * ⚠️ **The two overtime measures are the mirror image: worklog-only.** A claim hangs off an
     * hour (`0111`), so a task report has nothing to sum — the mockup's own menu disables them for
     * every source but `worklog`.
     */
    .refine((value) => (value.metric !== 'overtime' && value.metric !== 'overtimeCost') ||
    value.source === 'worklog', { error: 'the overtime metrics need the worklog source', path: ['metric'] })
    /**
     * C60 · ⚠️ **A money measure needs a money source.** A worklog has no revenue and a task has no
     * budget; the figure would be a confident zero on every row, which is the worst of the three
     * possible answers.
     */
    .refine((value) => (value.metric !== 'revenue' && value.metric !== 'budget') ||
    exports.MONEY_SOURCES.includes(value.source), {
    error: 'the money metrics need a contract, line item or occurrence source',
    path: ['metric'],
})
    /**
     * 17 Oct 2026 · ⚠️ **Munkaidő answers its own five measures and nothing else answers its four.**
     * A worklog has no expected hours, and a working day has no estimate or revenue.
     */
    .refine((value) => value.source === 'workday'
    ? exports.WORKDAY_METRICS.includes(value.metric)
    : !['expected', 'missing', 'leave', 'missingCost'].includes(value.metric), { error: 'that measure belongs to the other kind of source', path: ['metric'] })
    .refine((value) => value.source !== 'allocation' || exports.ALLOCATION_METRICS.includes(value.metric), {
    error: 'an allocation report measures planned hours or allocations',
    path: ['metric'],
})
    .refine((value) => value.filters?.softBookings === undefined || value.source === 'allocation', {
    error: 'only an allocation report has soft bookings',
    path: ['filters'],
})
    .refine((value) => value.source !== 'workday' || value.layout !== 'list', {
    error: 'working days have no list layout',
    path: ['layout'],
})
    /**
     * 17 Oct 2026 · ⚠️ **A timeline needs its spec, a source that has periods, rows and extras that
     * source can draw, and a bounded range** — columns run from one date to another, and *all time*
     * has no left edge to start them at.
     */
    .refine((value) => value.layout !== 'timeline' || timelineAllowed(value.source, value.timeline), {
    error: 'this source cannot be drawn over time that way',
    path: ['timeline'],
})
    .refine((value) => (value.layout !== 'timeline' && value.source !== 'workday') ||
    (value.period !== undefined && value.period !== 'custom' && value.period !== 'all') ||
    (value.periodFrom !== null && value.periodTo !== null), {
    /* ⚠️ Munkaidő too, in either layout: a working day has no *all time* to start from. */
    error: 'a timeline, and working days, need a period with both ends',
    path: ['periodFrom'],
})
    /**
     * C60 · ⚠️⚠️ **AND THE DIMENSION HAS TO BE ONE THE SOURCE CAN REACH.** Otherwise every row falls
     * into the `'none'` group and the report draws one bar labelled *"no value"* — which reads as
     * broken rather than as an impossible question.
     */
    .refine((value) => exports.DIMENSIONS_FOR[value.source].includes(value.dimension), {
    error: 'this source cannot group by that dimension',
    path: ['dimension'],
})
    /**
     * ⚠️ **An inverted range is REFUSED, where the mockup renders a note and runs anyway.** Its
     * *"a vég dátum a kezdés előtt van"* produces a permanently empty report; a saved snapshot that is
     * empty for a reason nobody can see is worse than a validation error at the boundary.
     */
    .refine((value) => value.periodFrom === null || value.periodTo === null || value.periodTo >= value.periodFrom, { error: 'the period ends before it starts', path: ['periodTo'] });
/** Whether a timeline spec fits its source — `TIMELINE_DIMENSIONS_FOR` and its extras' list. */
function timelineAllowed(source, spec) {
    if (spec === undefined)
        return false;
    const rows = timeline_1.TIMELINE_DIMENSIONS_FOR[source];
    const extras = timeline_1.TIMELINE_EXTRA_DIMENSIONS_FOR[source] ?? [];
    if (rows === undefined)
        return false;
    return (spec.rows.every((dimension) => rows.includes(dimension)) &&
        spec.extras.every((extra) => extras.includes(extra.dimension) && !spec.rows.includes(extra.dimension)));
}
/**
 * One group of the result.
 *
 * ⚠️ **All three figures on every row, not just the chosen metric.** The mockup's table shows
 * `Rögzített óra`, `Bejegyzés/Feladat` and `Becslés` side by side whatever the metric is — the metric
 * decides the chart and the sort, not what the table is allowed to say.
 */
exports.reportRowSchema = zod_1.z.object({
    /**
     * The group's identity — a uuid for a project, client, assignee or status; the raw value for a
     * metafield; and the literal `'none'` for the row that collects what has no group.
     *
     * ⚠️ `'none'` is a real group, not a rounding error: a task with no project, or a project with no
     * client, is the mockup's *"Belső"* row. Dropping it would make the total disagree with the sum.
     */
    key: zod_1.z.string(),
    /**
     * The group's label, or **null when the group is "no value in this dimension"**.
     *
     * ⚠️⚠️ **NULL IS A REAL ANSWER AND THE CLIENT WORDS IT.** A task with no project, or a project with
     * no client, is the mockup's *"Belső"* row — a user-facing string, so the server does not invent
     * one (CLAUDE.md §2 rule 7). It also cannot: *"Projekt nélkül"* and *"Belső"* are different
     * sentences for the same null, and only the screen knows which dimension it is drawing.
     */
    name: zod_1.z.string().nullable(),
    /**
     * The group's own colour, or null.
     *
     * ⚠️ **Null means "use an identity hue by position".** Projects, clients and statuses store a
     * colour (0014, 0015); members and metafield values do not, and the design system's `--id-1 … --id-8`
     * exist for exactly this — *"label projects, clients and people by identity, never by rank"*. The
     * server cannot name a CSS token, so it says null and the chart resolves it. The snapshot is
     * stored, so position is stable across reads.
     */
    color: zod_1.z.string().nullable(),
    /** ⚠️ `numeric` strings — they cross the pg driver as strings. Render through `formatHours`. */
    hours: zod_1.z.string(),
    count: zod_1.z.number().int().nonnegative(),
    /** ⚠️ Null on a worklog report: a time entry has no estimate. The table renders an em-dash. */
    estimate: zod_1.z.string().nullable(),
    /** The metric's own figure — `hours`, `estimate` or `count` as a string, so one column renders it. */
    value: zod_1.z.string(),
    /**
     * ⚠️ **Two percentages, and they answer different questions.** `barPercent` is against the
     * TALLEST row, because bars scaled to the total are all short and the shape is unreadable —
     * `hours-chart.tsx` records the same reasoning. `sharePercent` is against the total, which is what
     * a pie slice and the *"Arány"* column mean. Both arrive rounded so no two screens can disagree.
     */
    barPercent: zod_1.z.number().int().min(0).max(100),
    sharePercent: zod_1.z.number().int().min(0).max(100),
});
/** P-3's stored result. */
exports.reportSnapshotSchema = zod_1.z.object({
    rows: zod_1.z.array(exports.reportRowSchema),
    /** The metric's total across **the returned rows**, formatted like `value`. */
    total: zod_1.z.string(),
    /**
     * How many groups the run found, before `REPORT_MAX_GROUPS`.
     *
     * ⚠️ Greater than `rows.length` means the chart is showing the top few. The screen has to say so;
     * see `REPORT_MAX_GROUPS`.
     */
    groupCount: zod_1.z.number().int().nonnegative(),
    /**
     * ⚠⚠ **C59 · WHAT THE GROUPED ROWS THREW AWAY, FOR THE THREE CHARTS THAT NEED IT BACK.** A
     * calendar heatmap is a value per DAY and the heatmap is group × MONTH; both are axes the
     * definition does not carry, and neither can be recovered from rows that have already been summed
     * by project. So the run computes them alongside — in the same pass, over the same
     * permission-filtered set, so they cannot disagree with the rows above them.
     *
     * ⚠️ **Empty rather than absent when the source cannot produce them.** A task report has no
     * per-day series worth drawing; the picker reads the emptiness and disables those charts with a
     * reason, which is `chartRefusal` in `charts.ts`.
     */
    days: zod_1.z.array(zod_1.z.object({ on: wireDateSchema, value: zod_1.z.string() })),
    matrix: zod_1.z.object({
        /** `YYYY-MM`, ascending. Empty when the source has no date to bucket by. */
        months: zod_1.z.array(zod_1.z.string()),
        /** One per row of `rows`, in the same order, with one value per month. */
        rows: zod_1.z.array(zod_1.z.object({ key: zod_1.z.string(), values: zod_1.z.array(zod_1.z.string()) })),
    }),
});
exports.reportSchema = zod_1.z.object({
    id: zod_1.z.string(),
    /** `report_8a9b0c1d` — the public identifier `0194` mints beside the uuid; see `identifier.ts`. */
    identifier: (0, identifier_1.identifierSchema)('report'),
    name: zod_1.z.string(),
    definition: exports.reportDefinitionSchema,
    /**
     * ⚠️ **Null for a LIST report** (`0219`): it has no row limit, so it is never stored and runs when
     * it is opened. A summary report always has one (P-3).
     */
    snapshot: exports.reportSnapshotSchema.nullable(),
    /** ISO instant — the mockup's Refresh exists because this is shown. */
    ranAt: zod_1.z.string(),
    /**
     * ⚠️ **Present even though it is always the caller** (P-3), because a client that renders "by
     * Gábor" from a session object and a client that renders it from the row cannot disagree — and
     * because the day reports become shareable, the field is already there.
     */
    authorMemberId: zod_1.z.string(),
    authorName: zod_1.z.string(),
    /** Resolved names for whatever the definition references, so the list needs one request. */
    dimensionMetafieldName: zod_1.z.string().nullable(),
    filterProjectName: zod_1.z.string().nullable(),
    filterMemberName: zod_1.z.string().nullable(),
    filterMetafieldName: zod_1.z.string().nullable(),
    createdAt: zod_1.z.string(),
    version: zod_1.z.number().int(),
});
/**
 * Running a definition without saving it — the builder's live preview.
 *
 * ⚠️ **`POST`, not `GET`, and this is a read.** The definition has eleven fields including a free
 * string; as a query string it would be unreadable in a log and would hit URL limits the day
 * somebody adds a second filter. The mockup recomputes in the browser and so has no opinion.
 */
exports.runReportRequestSchema = exports.reportDefinitionSchema;
/**
 * A page of a LIST report — the builder's preview and a saved list report alike.
 *
 * ⚠️ **`POST` for a read, for `run`'s reason**, and offset-paged like every list (`page.ts`).
 */
exports.reportRowsRequestSchema = zod_1.z.object({
    definition: exports.reportDefinitionSchema,
    offset: zod_1.z.number().int().nonnegative().optional(),
    limit: zod_1.z.number().int().positive().optional(),
});
/** 17 Oct 2026 · a page of a TIMELINE report — the same body as a list's. */
exports.reportTimelineRequestSchema = exports.reportRowsRequestSchema;
/** The list layout's settings with every default applied — what the server and the screen read. */
function listSpecOf(definition) {
    return {
        layout: definition.layout ?? 'summary',
        columns: definition.columns ?? [],
        sort: definition.sort ?? null,
        filters: definition.filters ?? {},
    };
}
/**
 * Saving.
 *
 * ⚠️ **Saving RUNS the report**, because `snapshot` and `ran_at` are not null (P-3). The client does
 * not send its preview's numbers: a snapshot computed in a browser is a snapshot the server cannot
 * vouch for, and the whole point of P-3's privacy is that the numbers are the author's own
 * permission-filtered view.
 */
exports.createReportRequestSchema = zod_1.z.object({
    name: zod_1.z.string().trim().min(1).max(160),
    definition: exports.reportDefinitionSchema,
});
/**
 * Renaming, and re-saving a changed definition.
 *
 * ⚠️ Both optional, because the mockup has two separate gestures — *"Átnevezés"* in the kebab and
 * *"Mentés"* on an edited report — and one endpoint serving both must not require the other's
 * payload. A definition change re-runs and replaces the snapshot; a rename does not touch it.
 */
exports.updateReportRequestSchema = zod_1.z.object({
    name: zod_1.z.string().trim().min(1).max(160).optional(),
    definition: exports.reportDefinitionSchema.optional(),
    version: zod_1.z.number().int().nonnegative(),
});
//# sourceMappingURL=contract.js.map